import { filter, isEqual, isNil, omit, sortBy, toNumber } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { TensileTargetFormItem, TensileTransmitterFormValues } from '../types'

export interface TensileSupportWithTargets extends ElementSupportItem {
  targets: TensileTargetFormItem[]
}

/**
 * The tensile transmitter form only holds the slice of the graph it edits for one element
 * (`TensileTransmitterFormValues`). These helpers cut that slice out of the project graph and
 * merge it back in for saving.
 */

export const createSupportTarget = (
  element_guid: string,
  domain_guid: string,
  relative_position: number,
): ElementSupportItem =>
  ({
    guid: uuid(),
    element_guid,
    domain_guid,
    target_type: 'point',
    transmission_factor: 1,
    relative_position,
  } as unknown as ElementSupportItem)

export const createTensileSupport = (
  element_guid: string,
  domain_guid: string,
  relative_position: number,
): ElementSupportItem =>
  ({
    guid: uuid(),
    element_guid,
    domain_guid,
    support_type: 'point',
    accepted_transmission_types: ['ZPositive'],
    relative_position,
  } as unknown as ElementSupportItem)

/** The supports of `elementGuid`, sorted along the element */
export const getElementSupports = (graph: TensileTransmissionGraph, elementGuid: string) =>
  sortBy(filter(graph.element_supports, ['element_guid', elementGuid]), support =>
    toNumber(support.relative_position),
  )

/**
 * The form values for editing the supports of `elementGuid`. The targets are sorted along their
 * element once here; the form keeps that order while positions are edited
 */
export const toTensileFormValues = (
  graph: TensileTransmissionGraph,
  elementGuid: string,
): TensileTransmitterFormValues => {
  const supportGuids = new Set(getElementSupports(graph, elementGuid).map(({ guid }) => guid))
  const targetsByGuid = new Map(graph.element_targets.map(target => [target.guid, target]))

  const targets = sortBy(
    graph.support_targets.flatMap(({ support_guid, target_guid }) => {
      const target = targetsByGuid.get(target_guid)
      return supportGuids.has(support_guid) && target ? [{ ...target, support_guid }] : []
    }),
    target => toNumber(target.relative_position),
  )

  return { targets, addedSupports: [], removedSupportElementGuids: [] }
}

const toElementTarget = (target: TensileTargetFormItem): ElementSupportItem =>
  omit(target, 'support_guid')

/** Merges the edited slice of `elementGuid` (see `toTensileFormValues`) into the project graph */
export const applyTensileFormValues = (
  graph: TensileTransmissionGraph,
  elementGuid: string,
  { targets, addedSupports, removedSupportElementGuids }: TensileTransmitterFormValues,
): TensileTransmissionGraph => {
  const supportGuids = new Set(getElementSupports(graph, elementGuid).map(({ guid }) => guid))
  const savedTargetGuids = new Set(
    graph.support_targets
      .filter(({ support_guid }) => supportGuids.has(support_guid))
      .map(({ target_guid }) => target_guid),
  )
  const editedTargetGuids = new Set(targets.map(({ guid }) => guid))
  const removedSupportElements = new Set(removedSupportElementGuids)
  const removedSupportGuids = new Set(
    graph.element_supports
      .filter(({ element_guid }) => removedSupportElements.has(element_guid))
      .map(({ guid }) => guid),
  )

  // Links of other supports stay, unless their support is dropped or they point to a target
  // deleted here
  const keptLinks = graph.support_targets.filter(
    ({ support_guid, target_guid }) =>
      !supportGuids.has(support_guid) &&
      !removedSupportGuids.has(support_guid) &&
      (editedTargetGuids.has(target_guid) || !savedTargetGuids.has(target_guid)),
  )
  const keptLinkTargetGuids = new Set(keptLinks.map(({ target_guid }) => target_guid))
  // The targets of dropped supports go with them, unless another support still links them
  const droppedTargetGuids = new Set(
    graph.support_targets
      .filter(
        ({ support_guid, target_guid }) =>
          removedSupportGuids.has(support_guid) && !keptLinkTargetGuids.has(target_guid),
      )
      .map(({ target_guid }) => target_guid),
  )

  return {
    element_supports: [
      ...graph.element_supports.filter(({ guid }) => !removedSupportGuids.has(guid)),
      ...addedSupports,
    ],
    element_targets: [
      ...graph.element_targets.filter(
        ({ guid }) => !savedTargetGuids.has(guid) && !droppedTargetGuids.has(guid),
      ),
      ...targets.map(toElementTarget),
    ],
    support_targets: [
      ...keptLinks,
      ...targets.map(({ guid, support_guid }) => ({ support_guid, target_guid: guid })),
    ],
  }
}

/**
 * `supports` with their targets (from the form), in the form's order: not re-sorted by the edited
 * positions, so the list doesn't reorder while typing
 */
export const getSupportsWithTargets = (
  supports: ElementSupportItem[],
  targets: TensileTargetFormItem[],
): TensileSupportWithTargets[] =>
  supports.map(support => ({
    ...support,
    targets: filter(targets, ['support_guid', support.guid]),
  }))

/** A graph of only the edited supports and their targets, to preview the unsaved edits */
export const getTensilePreviewGraph = (
  supports: ElementSupportItem[],
  targets: TensileTargetFormItem[],
): TensileTransmissionGraph => ({
  element_supports: supports,
  element_targets: targets.map(toElementTarget),
  support_targets: targets.map(({ guid, support_guid }) => ({ support_guid, target_guid: guid })),
})

/** Whether the graph still has a target on `elementGuid` that a support links to */
export const hasLinkedTargetOnElement = (graph: TensileTransmissionGraph, elementGuid: string) => {
  const linkedTargetGuids = new Set(graph.support_targets.map(({ target_guid }) => target_guid))
  return graph.element_targets.some(
    ({ guid, element_guid }) => element_guid === elementGuid && linkedTargetGuids.has(guid),
  )
}

/** Whether a support on `elementGuid` still passes tension on to a target */
export const hasSupportWithTargetsOnElement = (
  graph: TensileTransmissionGraph,
  elementGuid: string,
) => {
  const supportGuids = new Set(getElementSupports(graph, elementGuid).map(({ guid }) => guid))
  return graph.support_targets.some(({ support_guid }) => supportGuids.has(support_guid))
}

const toNumberIfSet = <T>(value: T) => (isNil(value) ? value : toNumber(value))

/**
 * Number inputs hold their values as strings, so the targets' numeric fields are converted to
 * compare the form values. Only for comparing: the saved graph must keep the exact decimal
 * strings, floats lose precision the backend's interval checks rely on
 */
export const normalizeTensileFormValues = (
  values: TensileTransmitterFormValues,
): TensileTransmitterFormValues => ({
  ...values,
  targets: values.targets.map(target => ({
    ...target,
    transmission_factor: toNumber(target.transmission_factor),
    relative_position: toNumberIfSet(target.relative_position) as string | undefined,
  })),
})

export const isTensileFormValuesEqual = (
  a: TensileTransmitterFormValues,
  b: TensileTransmitterFormValues,
) => isEqual(normalizeTensileFormValues(a), normalizeTensileFormValues(b))
